import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { ChevronDown, UserPlus } from '../../icons';
import { Avatar } from '../../components/shared';
import { listTeamMembers, invalidateTeamMembers, type TeamMemberDto } from '../../api/project-teams';
import { TeamInvitePickerModal } from '../ProjectPage/TeamInvitePickerModal';
import { useAuthStore } from '../../store/authStore';
import { tTask, getTaskLang, subscribeTaskLang } from '../../lib/task-i18n';
import './TaskBoardShell.css';

interface TeamMemberBadgeProps {
  /** 프로젝트 소속 팀. null(개인 프로젝트)이면 아무것도 렌더하지 않는다. */
  teamId: string | null;
}

/**
 * TeamMemberBadge (2026-08-18 시안 ④) — 헤더의 **소형 팀 배지**.
 *
 * 종전 시안에서 보드 위를 차지하던 '내 팀' 카드를 헤더 배지 한 칸으로 접은 것.
 * 배지 = 아바타 스택(3) + `내 팀 · N명` + ▾, 누르면 팀원 드롭다운(초대 링크 + 전체 명단).
 *
 * 소스 회귀 금지(CLAUDE.md): 신규 파일 · additive. 멤버는 기존 `listTeamMembers`(TTL 캐시)
 * 를 그대로 쓰므로 서버 무변경이고 TeamGantt 와 요청이 합쳐진다.
 *
 * ## ⭐⭐ 2026-10-10 — ★ 초대가 ⇒ ⭐ «화면을 떠나지 않는다» (정본 dd05a6fcd · 그쪽 2026-08-19)
 *
 * ⛔ 종전 줄 ⇒ 「Router-free 계약 유지 — ★ 초대 링크는 ⭐ 정적 anchor」
 *   ⇒ ⇒ ⭐ 그 anchor 가 ★ `/project/teams?team=…` 로 ⇒ ⛔ ★ «화면을 떠났다».
 *   ⇒ ⇒ ★ 정본 주석이 그 값을 적었다: 「보드를 보다 ★ 사람 하나 넣으려던 흐름이
 *      ⇒ ⇒ ⭐ 그때마다 끊겼다. 같은 자리에서 고르고 닫도록 ★ 모달을 연다」
 *   ⇒ ⇒ ⇒ ★ 이 레포가 ⭐ 세 번 지적받은 그 꼴이다 —
 *      ⇒ [[interim-wiring-to-old-screen-reads-as-bug]] 「옛 화면으로 가는 임시 배선 ＝ 사용자에겐 버그」.
 *
 * ⇒ ✅ 그래서 ★ `<a href>` 를 ⇒ ⭐ `<button>` ＋ `TeamInvitePickerModal` 로 바꿨다.
 *   ⇒ ⭐ **Router-free 계약은 ★ 그대로다** — ⇒ 그 모달도 ★ 라우터를 ⭐ 쓰지 않는다
 *     (⇒ `createPortal` 로 띄우고 ★ 돌아갈 곳이 ⭐ 없다). ⇒ ⇒ 그래서 ★ 종전 줄을 ⭐ 고쳤을 뿐
 *     ⇒ ⇒ ★ «계약을 깬» 것이 ⛔ 아니다.
 *   ⇒ ⭐ 서버 일 ★ 0 — ⇒ 그 모달이 쓰는 api ⭐ 여덟이 ★ 전부 우리에 ⭐ 있고(실측)
 *     ⇒ ⇒ 서버 blob 셋(accept-invite · invite-token ×2)이 ★ ⭐ 글자까지 같다.
 *   ⇒ ⚠ 정본은 배지 단추에 `data-tip={label}` 을 쓰지만 ⇒ ⛔ 우리 트리엔 ★ 그것을 그리는
 *     ⇒ ⇒ CSS 가 ⭐ 0 파일이다(판 A 에서 쟀다) ⇒ ⇒ ✅ `title={label}` 을 ★ 그대로 뒀다.
 */
export function TeamMemberBadge({ teamId }: TeamMemberBadgeProps) {
  useSyncExternalStore(subscribeTaskLang, getTaskLang, getTaskLang);
  const authUser = useAuthStore((s) => s.user);
  const myId = authUser?.id != null ? String(authUser.id) : '';
  const [members, setMembers] = useState<TeamMemberDto[]>([]);
  const [open, setOpen] = useState(false);
  /** ⭐ 초대 모달이 떠 있나 — ⇒ ★ 드롭다운은 닫고 모달만 띄운다. */
  const [inviting, setInviting] = useState(false);
  /**
   * ⭐ 초대가 끝난 뒤 ★ 멤버를 ⭐ «다시 읽게» 하는 수.
   *
   * ⚠ `listTeamMembers` 는 ★ 60초 TTL 캐시다 ⇒ ⇒ ⛔ 그래서 `invalidateTeamMembers` 로
   *   ⇒ 캐시를 버린 «뒤»에 ★ 이 수를 올려 ⇒ ⭐ 효과를 다시 돈다.
   *   ⇒ ⇒ ★ 둘 중 ⭐ 하나만 하면 ⇒ ⛔ 「초대했는데 ★ 명단이 그대로」가 된다
   *      (캐시만 버리면 ★ 다시 안 읽고 · 수만 올리면 ★ 캐시가 옛 값을 준다).
   */
  const [reload, setReload] = useState(0);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let alive = true;
    if (!teamId) { setMembers([]); return; }
    void listTeamMembers(teamId)
      .then((list) => { if (alive) setMembers(Array.isArray(list) ? list : []); })
      .catch(() => { if (alive) setMembers([]); });
    return () => { alive = false; };
  }, [teamId, reload]);

  // 팀이 바뀌면 열려 있던 드롭다운은 닫는다(다른 팀의 명단이 잠깐 남는 것 방지).
  useEffect(() => { setOpen(false); setInviting(false); }, [teamId]);

  // 바깥 클릭 · Esc 로 닫기.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!teamId) return null;

  // 나를 맨 앞으로 — 드롭다운 첫 줄이 '나' 가 되도록(시안).
  const ordered = [...members].sort((a, b) => {
    if (a.userId === myId) return -1;
    if (b.userId === myId) return 1;
    return 0;
  });
  const shown = ordered.slice(0, 3);
  const label = tTask('task.teamdrop.summary', { n: String(members.length) });

  return (
    <div className="tb-team" ref={wrapRef}>
      <button
        type="button"
        className={`tb-team-badge${open ? ' tb-team-badge--on' : ''}`}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={tTask('task.teamdrop.open_aria')}
        title={label}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="tb-team-stack" aria-hidden="true">
          {shown.map((m) => (
            <Avatar key={m.userId} userId={Number(m.userId) || 0} name={m.name} src={m.avatarUrl} size="xxs" />
          ))}
        </span>
        <span className="tb-team-label">{label}</span>
        <ChevronDown size={13} aria-hidden />
      </button>

      {open && (
        <div className="tb-team-drop" role="menu" aria-label={label}>
          <div className="tb-team-drop-head">
            <span className="tb-team-drop-title">{label}</span>
            {/* ⭐ 2026-10-10 — ★ 종전에는 `<a href="/project/teams?team=…">` 였다.
                ⇒ ⛔ 그러면 ★ 보드를 ⭐ «떠난다» ⇒ ⇒ 같은 자리에서 끝내도록 ★ 모달을 연다. */}
            <button
              type="button"
              className="tb-team-invite"
              title={tTask('task.teamdrop.invite')}
              onClick={() => { setOpen(false); setInviting(true); }}
            >
              <UserPlus size={12} aria-hidden />
              {tTask('task.teamdrop.invite')}
            </button>
          </div>
          <ul className="tb-team-list scrollbar-thin">
            {ordered.length === 0 && (
              <li className="tb-team-empty" role="none">{tTask('task.teamdrop.empty')}</li>
            )}
            {ordered.map((m) => (
              <li key={m.userId} className="tb-team-item" role="menuitem">
                <Avatar userId={Number(m.userId) || 0} name={m.name} src={m.avatarUrl} size="xs" />
                <span className="tb-team-name">
                  {m.userId === myId ? `${m.name} · ${tTask('task.teamdrop.me')}` : m.name}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ⭐ 초대 고르개 — ★ 드롭다운과 ⭐ «따로» 띄운다(드롭다운은 닫힌 상태다).
          ⇒ ⚠ onInvited 가 ★ ⭐ 둘을 다 해야 한다 — ⇒ 캐시 버리기 ＋ 다시 읽기(위 reload 주석). */}
      {inviting && (
        <TeamInvitePickerModal
          teamId={teamId}
          onClose={() => setInviting(false)}
          onInvited={() => {
            setInviting(false);
            invalidateTeamMembers(teamId);
            setReload((n) => n + 1);
          }}
        />
      )}
    </div>
  );
}
